import * as React from 'react';
import type { BaseRange } from 'slate';
import { ReactEditor } from 'slate-react';

import { autoPositionOverlay, usePopOver } from '@xing-com/pop-over';
import { Portal } from '@xing-com/portal';
import { scale410 } from '@xing-com/tokens';
import { useMediaListener } from '@xing-com/util';

import { MENTION_PORTAL_ID } from './constants';
import { PopoverContent } from './popover-content';
import type { PopoverContentProps } from './popover-content';
import { Styled } from './select-mention.style';

export type SelectMentionProps = {
  containerId?: string;
  isInlinePortal?: boolean;
  popoverContentProps: PopoverContentProps;
  target: BaseRange | null;
  editor: ReactEditor | null;
};

export const SelectMention = ({
  containerId = MENTION_PORTAL_ID,
  isInlinePortal = false,
  popoverContentProps,
  target,
  editor,
}: SelectMentionProps) => {
  const matchedMedia = useMediaListener();
  const ref = React.useRef<HTMLDivElement | null>(null);
  const [popoverLocation, setPopoverLocation] = React.useState({
    top: '',
    left: '',
  });

  const isHandheld = matchedMedia === 'handheld';
  const popOver = usePopOver();

  React.useEffect(() => {
    if (!(isHandheld && isInlinePortal))
      if (target) {
        popOver.handleShow();
      } else {
        popOver.handleHide();
      }
  }, [target, popOver, isHandheld, isInlinePortal]);

  // Positions the popover near the "@"
  React.useEffect(() => {
    if (target && editor) {
      const el = ref.current;

      if (el && !(isHandheld && isInlinePortal)) {
        const domRange = ReactEditor.toDOMRange(editor, target);
        const triggerRect = domRange.getBoundingClientRect();
        const overlayRect = el.getBoundingClientRect();

        // Listens for the available space to the left and right
        // To avoid overflowing the popover
        const collisions = {
          right: triggerRect.left + overlayRect.width > window.innerWidth,
          left: overlayRect.width > triggerRect.right,
        };
        const isLeft = collisions.right && !collisions.left;

        const { top = '', left = '' } = autoPositionOverlay(
          triggerRect,
          overlayRect
        ) as { top?: string; left?: string };

        setPopoverLocation({
          top,
          left: isLeft
            ? `calc(${triggerRect.left + triggerRect.width}px - ${scale410})`
            : left,
        });
      }
    }
  }, [editor, target, isHandheld, isInlinePortal]);

  return isHandheld && isInlinePortal ? (
    <Portal {...{ containerId }}>
      {target && (
        <Styled.InlinePopOver>
          <PopoverContent {...popoverContentProps} />
        </Styled.InlinePopOver>
      )}
    </Portal>
  ) : (
    <div {...{ ref }}>
      <Styled.MentionPopover
        isInFlow
        isStatic
        show={popOver.show}
        $top={popoverLocation.top}
        $left={popoverLocation.left}
      >
        <PopoverContent {...popoverContentProps} />
      </Styled.MentionPopover>
    </div>
  );
};
